import { ORDER_PAYMENT } from '@hive/status'
import { useEffect, useRef, useState } from 'react'

import {
  STATUS_PAYMENT_METHOD_NOT_READY_YET
} from '@features/payment/lib/const'

import useAppWebsocket from './useAppWebsocket'


type Message = {
  status: number
  metadata?: Record<string, unknown>
}

type Subscription = {
  on: (event: string, callback: (message: Message) => void) => void
  emit: (event: string, data: { operationId: string }) => void
  close: () => void
}

export default function usePaymentVerificator ({
  operationId,
}: {
  operationId: string | null
}) {
  const [ state, setState ] = useState<{
    verified: boolean
    rejected: boolean
    metadata: Record<string, unknown>
    isLoading: boolean
  }>({
    verified: false,
    rejected: false,
    metadata: {},
    isLoading: true,
  })

  const pingTimer = useRef<NodeJS.Timeout | null>(null)
  const { socket, ready } = useAppWebsocket()

  useEffect(() => {
    if (!operationId || !ready || !socket) return

    let subscription: Subscription | null = null

    try {
      // operationId already contains the full topic path from server
      const topic = `payment-status:${operationId}`
      
      subscription = socket.subscribe(topic)

      subscription.on('statusChanged', (message: Message) => {
        const { status, metadata = {} } = message

        if (status === STATUS_PAYMENT_METHOD_NOT_READY_YET) {
          return
        }

        if (pingTimer.current) {
          clearInterval(pingTimer.current)
        }

        if (status === ORDER_PAYMENT.ARRIVED) {
          setState((prevState) => ({
            ...prevState,
            verified: true,
            metadata,
            isLoading: false,
          }))
        }
        else if (status === ORDER_PAYMENT.REJECTED) {
          setState((prevState) => ({
            ...prevState,
            rejected: true,
            metadata,
            isLoading: false,
          }))
        }
      })

      subscription.on('ready', () => {
        pingTimer.current = setInterval(() => {
          subscription?.emit('statusCheck', { operationId: operationId })
        }, 5000)
      })

      subscription.on('error', (error: Message) => {
        console.error('[usePaymentVerificator] Subscription error:', error)
        if (pingTimer.current) {
          clearInterval(pingTimer.current)
        }
      })

      subscription.on('close', () => {
        if (pingTimer.current) {
          clearInterval(pingTimer.current)
        }
      })
    }
    catch (error) {
      console.error('WebSocket subscription error:', error)
    }

    return () => {
      if (pingTimer.current) {
        clearInterval(pingTimer.current)
      }
      if (subscription) {
        try {
          subscription.close()
        }
        catch (error) {
          console.warn('Error closing subscription:', error)
        }
      }
    }
  }, [ operationId, ready, socket ])

  return state
}
